import { useSearchParams } from 'react-router-dom'
import React, { useEffect, useRef, useState } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import { firebase_auth, firebase_firestore } from '../common/firebaseConfig'
import { useDocumentData } from 'react-firebase-hooks/firestore'
import { doc } from 'firebase/firestore'
import { signInWithCustomToken, signOut } from 'firebase/auth'
import LoadingShell from '../components/LoadingShell'
import ContactSupport from '../components/ContactSupport'
import ReactGA from 'react-ga4'
import { isTokenForCurrentUser } from '../common/utils/tokenUtils'
import { API_BASE } from '../api/callableRef'
import posthog, { isPostHogEnabled, resumeDeferredSessionRecording } from '../posthog'

// Same-origin /api path so the Hosting rewrite routes the SSO id-token exchange to
// gc-public. API_BASE is same-origin when served from Hosting, the absolute dev/prod
// Hosting origin from localhost.
async function exchangeIdToken(idToken: string) {
  const response = await fetch(`${API_BASE}/api/validateIdToken`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token: idToken }),
  })
  const data = await response.json()
  return signInWithCustomToken(firebase_auth, data.authToken)
}

export default function GreenChartSubscriptionRouteGuard({ children }: any) {
  const [searchParams, setSearchParams] = useSearchParams()

  const [hasAccess, setHasAccess] = useState(false)
  const [user, loading] = useAuthState(firebase_auth)
  const [usermeta, loadingUserMeta] = useDocumentData(
    doc(firebase_firestore, `usermeta/${user?.uid}`)
  )
  const [validatingToken, setValidatingToken] = useState(false)
  const identifiedUserIdRef = useRef<string>()
  const handledTokenRef = useRef<string>()

  useEffect(() => {
    const idToken = searchParams.get('idToken')
    const authToken = searchParams.get('authToken')
    const token = idToken ?? authToken

    if (token) {
      // Drop the credentials from the address bar so analytics and history never see them.
      const cleanedParams = new URLSearchParams(searchParams)
      cleanedParams.delete('idToken')
      cleanedParams.delete('authToken')
      setSearchParams(cleanedParams, { replace: true })
    } else {
      resumeDeferredSessionRecording()
    }

    if (!token) {
      // No token in the URL: keep whatever session Firebase persisted.
      if (user) {
        setHasAccess(true)
      }
      return
    }

    // A token for the already signed-in user needs no exchange.
    if (user && isTokenForCurrentUser(token, user.uid)) {
      setHasAccess(true)
      return
    }

    // Exchange each token once; the URL cleanup and auth-state changes both re-run this effect.
    if (handledTokenRef.current === token) {
      return
    }
    handledTokenRef.current = token

    // A token in the URL replaces whichever session was persisted.
    setValidatingToken(true)
    const signIn = idToken ? exchangeIdToken(idToken) : signInWithCustomToken(firebase_auth, token)
    signIn
      .then(() => setHasAccess(true))
      .catch(async (error) => {
        console.error('Error:', error)
        // A failed exchange must not fall back to a different user's persisted session.
        const current = firebase_auth.currentUser
        const keepSession = !!current && isTokenForCurrentUser(token, current.uid)
        if (current && !keepSession) {
          await signOut(firebase_auth)
        }
        setHasAccess(keepSession)
      })
      .finally(() => setValidatingToken(false))
  }, [user, searchParams, setSearchParams])

  useEffect(() => {
    if (usermeta) {
      ReactGA.set({ user_id: usermeta.gcid })
    }
  }, [usermeta])

  useEffect(() => {
    if (!isPostHogEnabled) {
      return
    }

    if (!user) {
      if (identifiedUserIdRef.current) {
        posthog.reset()
        identifiedUserIdRef.current = undefined
      }
      return
    }

    if (typeof usermeta?.gcid !== 'string' || !usermeta.gcid) {
      return
    }

    if (identifiedUserIdRef.current === usermeta.gcid) {
      return
    }

    if (identifiedUserIdRef.current) {
      posthog.reset()
    }

    const personProperties: Record<string, string> = {}
    if (user.email) {
      personProperties.email = user.email
    }
    if (user.displayName) {
      personProperties.name = user.displayName
    }

    posthog.identify(usermeta.gcid, personProperties)
    identifiedUserIdRef.current = usermeta.gcid
  }, [user, usermeta])

  if (loading || loadingUserMeta || validatingToken) {
    return <LoadingShell />
  }

  if (hasAccess && user && usermeta) {
    return children
  } else {
    return <ContactSupport />
  }
}
